import { useState } from "react";
import clsx from "clsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  SLURRY_MANUAL_KINDS,
  marshApparentViscosity,
  type SlurryManualKind,
} from "@tbm/shared";
import { api } from "../lib/api";
import { localNaiveInputToUtcIso, useTimezone, utcToLocalNaive } from "../lib/datetime";
import { toLocalInputInTz } from "./DownloadDrawer";

const KINDS = Object.keys(SLURRY_MANUAL_KINDS) as SlurryManualKind[];

/**
 * Manual slurry readings on /slurry: add (with an optional note) and
 * delete. The points themselves are drawn on the chart by
 * DriveParameters, over the matching slurry channel.
 */
export function SlurryManualReadings() {
  const qc = useQueryClient();
  const tz = useTimezone();
  const [kind, setKind] = useState<SlurryManualKind>("plasticViscosity");
  const [value, setValue] = useState("");
  const [density, setDensity] = useState("");
  const [tsInput, setTsInput] = useState(() => toLocalInputInTz(new Date(), tz));
  const [note, setNote] = useState("");

  const list = useQuery({
    queryKey: ["slurry-manual", "recent"],
    queryFn: () => api.slurryManual.list({ from: "-30d" }),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["slurry-manual"] });

  const add = useMutation({
    mutationFn: () =>
      api.slurryManual.create({
        kind,
        ts: localNaiveInputToUtcIso(tsInput, tz),
        value: Number(value),
        density: kind === "marshFunnel" ? Number(density) : undefined,
        note,
      }),
    onSuccess: () => {
      setValue("");
      setNote("");
      setTsInput(toLocalInputInTz(new Date(), tz));
      void refresh();
    },
  });
  const del = useMutation({
    mutationFn: (id: number) => api.slurryManual.delete(id),
    onSuccess: () => void refresh(),
  });

  const isMarsh = kind === "marshFunnel";
  const unit = SLURRY_MANUAL_KINDS[kind].unit;
  const av = isMarsh ? marshApparentViscosity(Number(value), Number(density)) : null;
  const canAdd =
    value.trim() !== "" &&
    Number.isFinite(Number(value)) &&
    !!tsInput &&
    (!isMarsh || av != null) &&
    !add.isPending;

  const box = "bg-surface2 border border-border1 rounded px-2 py-1 text-[11px] text-text focus:outline-none focus:border-wb/60";
  const readings = list.data?.readings ?? [];

  return (
    <section className="card px-5 py-4 flex flex-col gap-3">
      <span className="text-[10px] uppercase tracking-[0.22em] text-dim">Manual readings</span>

      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (canAdd) add.mutate();
        }}
      >
        <select value={kind} onChange={(e) => setKind(e.target.value as SlurryManualKind)} className={box}>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {SLURRY_MANUAL_KINDS[k].label}
            </option>
          ))}
        </select>
        <div className={clsx(box, "inline-flex items-center gap-1 px-0 py-0")}>
          <input
            type="number"
            inputMode="decimal"
            step="any"
            placeholder={isMarsh ? "time" : "value"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="bg-transparent w-20 px-2 py-1 focus:outline-none"
          />
          <span className="pr-2 text-dim">{unit}</span>
        </div>
        {isMarsh && (
          <div className={clsx(box, "inline-flex items-center gap-1 px-0 py-0")}>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              placeholder="density"
              value={density}
              onChange={(e) => setDensity(e.target.value)}
              className="bg-transparent w-20 px-2 py-1 focus:outline-none"
            />
            <span className="pr-2 text-dim">g/cm³</span>
          </div>
        )}
        <input
          type="datetime-local"
          value={tsInput}
          onChange={(e) => setTsInput(e.target.value)}
          className={box}
        />
        <input
          type="text"
          placeholder="Note"
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className={clsx(box, "flex-1 min-w-[160px]")}
        />
        <button
          type="submit"
          disabled={!canAdd}
          className="px-3 py-1.5 rounded border text-[11px] tracking-wider uppercase bg-surface2 border-border1 text-muted hover:bg-surface3 hover:text-text disabled:opacity-50 disabled:pointer-events-none"
        >
          {add.isPending ? "Adding…" : "Add"}
        </button>
        {isMarsh && (
          <span className="text-[11px] text-muted mono-nums">
            AV {av != null ? `${av.toFixed(1)} cP` : "—"}
          </span>
        )}
      </form>
      {add.error && <div className="text-[11px] text-wb">{(add.error as Error).message}</div>}

      {readings.length > 0 && (
        <table className="w-full text-[11.5px]">
          <tbody>
            {readings.slice(0, 20).map((r) => {
              const k = SLURRY_MANUAL_KINDS[r.kind];
              const t = utcToLocalNaive(r.ts, tz)?.replace("T", " ").slice(0, 16) ?? r.ts;
              return (
                <tr key={r.id} className="border-t border-border1">
                  <td className="py-1.5 pr-4 text-muted mono-nums whitespace-nowrap">{t}</td>
                  <td className="py-1.5 pr-4 text-text whitespace-nowrap">{k.label}</td>
                  <td className="py-1.5 pr-4 text-text mono-nums whitespace-nowrap">
                    {r.value} {k.unit}
                    {r.kind === "marshFunnel" && r.apparentViscosity != null && (
                      <span className="text-muted"> → {r.apparentViscosity.toFixed(1)} cP</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-4 text-muted w-full">{r.note}</td>
                  <td className="py-1.5 text-right">
                    <button
                      type="button"
                      disabled={del.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete ${k.label} reading at ${t}?`)) del.mutate(r.id);
                      }}
                      className="text-[10.5px] uppercase tracking-wider text-dim hover:text-wb"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
